import React, { useState, useEffect, useCallback } from "react";
import { FaPlus, FaTrash, FaSave, FaTimes, FaEdit } from "react-icons/fa";
import "../styles/ServiceManager.css";

// 1. FIXED API URL to match your local environment
// const API = "http://localhost:5000";
const API = "https://backend.afrivisionconsultants.com";

export default function ServiceManager({ token }) {
  const [serviceGroups, setServiceGroups] = useState([]);
  const [loading, setLoading] = useState(false);

  // --- MODAL STATE ---
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // 'create' or 'edit'
  const [activeGroupId, setActiveGroupId] = useState(null); 
  const [editingCardId, setEditingCardId] = useState(null); 
  
  // --- FORM STATES ---
  const [cardForm, setCardForm] = useState({ title: "", desc: "", link: "" });
  const [cardImage, setCardImage] = useState(null);
  
  const [editingGroupTitle, setEditingGroupTitle] = useState(null); 
  const [tempGroupTitle, setTempGroupTitle] = useState(""); 
  const [newGroupTitle, setNewGroupTitle] = useState("");

  // Regular headers for JSON data
  const getHeaders = useCallback(() => ({ 
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json"
  }), [token]);

  // --- FETCH DATA ---
  const fetchServices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/services`, { headers: getHeaders() });
      if (res.ok) {
          const data = await res.json();
          setServiceGroups(data);
      }
    } catch (e) { console.error("Fetch error:", e); } 
    finally { setLoading(false); }
  }, [getHeaders]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  // --- GROUP ACTIONS ---
  const handleAddGroup = async () => {
      if (!newGroupTitle.trim()) {
          alert("Please enter a group title first!");
          return;
      }
      
      try {
          const res = await fetch(`${API}/admin/services/group`, {
              method: "POST",
              headers: getHeaders(),
              body: JSON.stringify({ title: newGroupTitle })
          });
          
          if (res.ok) {
              setNewGroupTitle("");
              fetchServices();
          } else {
              const errorData = await res.json().catch(() => ({}));
              alert(`Failed to add group. Server responded with status: ${res.status}. ${errorData.error || ""}`);
          }
      } catch (e) { 
          console.error(e); 
          alert("Network error! Make sure your Node.js backend server is running.");
      }
  };

  const handleDeleteGroup = async (id) => {
      if (!window.confirm("Are you sure? This will delete the group and ALL its cards.")) return;
      try {
          const res = await fetch(`${API}/admin/services/group/${id}`, {
              method: "DELETE", headers: getHeaders()
          });
          if (res.ok) fetchServices();
      } catch (e) { console.error(e); }
  };

  const handleRenameGroup = async (id) => {
      try {
          const res = await fetch(`${API}/admin/services/group/${id}`, {
              method: "PUT",
              headers: getHeaders(),
              body: JSON.stringify({ title: tempGroupTitle })
          });
          if (res.ok) {
              setEditingGroupTitle(null);
              fetchServices();
          }
      } catch (e) { console.error(e); }
  };

  // --- CARD ACTIONS ---
  const openCreateModal = (groupId) => {
      setModalMode("create");
      setActiveGroupId(groupId);
      setCardForm({ title: "", desc: "", link: "" });
      setCardImage(null);
      setIsModalOpen(true);
  };

  const openEditModal = (card) => {
      setModalMode("edit");
      setEditingCardId(card.id);
      setCardForm({ title: card.title, desc: card.desc, link: card.link || "" });
      setCardImage(null);
      setIsModalOpen(true);
  };

  const handleSaveCard = async () => {
      const formData = new FormData();
      formData.append("title", cardForm.title);
      formData.append("desc", cardForm.desc);
      formData.append("link", cardForm.link);
      if (cardImage) formData.append("image", cardImage);

      try {
          let url = `${API}/admin/services/item`;
          let method = "POST";
          
          if (modalMode === "create") {
              formData.append("groupId", activeGroupId);
          } else {
              url = `${API}/admin/services/item/${editingCardId}`;
              method = "PUT";
          }

          const res = await fetch(url, {
              method,
              headers: { "Authorization": `Bearer ${token}` }, 
              body: formData
          });

          if (res.ok) {
              setIsModalOpen(false);
              fetchServices();
          } else {
              alert("Failed to save card. Check console for details.");
          }
      } catch (e) { console.error(e); }
  };

  const handleDeleteCard = async (id) => {
      if (!window.confirm("Delete this card?")) return;
      try {
          const res = await fetch(`${API}/admin/services/item/${id}`, {
              method: "DELETE", headers: getHeaders()
          });
          if (res.ok) fetchServices();
      } catch (e) { console.error(e); }
  };

  return (
    <div className="manage-section">
      <h2>Manage Services & Products</h2>
      
      {/* ADD GROUP */}
      <div className="add-input form-row mb-15">
        <input 
          placeholder="New Category / Group Title (e.g., Accounting Services)" 
          value={newGroupTitle} 
          onChange={(e) => setNewGroupTitle(e.target.value)} 
          className="flex-1"
        />
        <button onClick={handleAddGroup} className="btn-success"><FaPlus /> Add Group</button>
      </div>

      {loading ? <p>Loading services...</p> : (
        <div className="vertical-list-spaced">
          {serviceGroups.map(group => (
            <div key={group.id} className="service-group-card">
              
              {/* GROUP HEADER */}
              <div className="service-group-header">
                {editingGroupTitle === group.id ? (
                  <div className="form-row edit-group-form">
                    <input className="flex-1" value={tempGroupTitle} onChange={(e) => setTempGroupTitle(e.target.value)} />
                    <button className="btn-success" onClick={() => handleRenameGroup(group.id)}><FaSave /> Save</button>
                    <button className="btn-secondary" onClick={() => setEditingGroupTitle(null)}><FaTimes /></button>
                  </div>
                ) : (
                  <h3>{group.title}</h3>
                )}
                
                <div className="group-actions">
                  {editingGroupTitle !== group.id && (
                    <button className="btn-action btn-edit" onClick={() => { setEditingGroupTitle(group.id); setTempGroupTitle(group.title); }}>
                      <FaEdit /> Rename
                    </button>
                  )}
                  <button className="btn-action btn-delete" onClick={() => handleDeleteGroup(group.id)}>
                    <FaTrash /> Delete
                  </button>
                  <button className="btn-action btn-add" onClick={() => openCreateModal(group.id)}>
                    <FaPlus /> Add Card
                  </button>
                </div>
              </div>

              {/* GROUP CARDS */}
              <div className="cards-grid">
                {group.ServiceItems && group.ServiceItems.map(item => (
                  <div key={item.id} className="service-card-item">
                    {item.image && (
                      <img 
                        src={item.image.startsWith("http") ? item.image : `${API}${item.image}`} 
                        alt={item.title} 
                        className="service-card-img" 
                      />
                    )}
                    <h4 className="service-card-title">{item.title}</h4>
                    <p className="service-card-desc">{item.desc}</p>
                    <div className="service-card-actions">
                      <button className="btn-warning" onClick={() => openEditModal(item)}><FaEdit /> Edit</button>
                      <button className="delete-btn" onClick={() => handleDeleteCard(item.id)}><FaTrash /> Trash</button>
                    </div>
                  </div>
                ))}
                {(!group.ServiceItems || group.ServiceItems.length === 0) && (
                  <p className="empty-group-text">No cards added to this group yet.</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* --- SHARED MODAL (Create & Edit) --- */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="form-column modal-content">
            <h3>{modalMode === "create" ? "Add New Card" : "Edit Card Details"}</h3>
            
            <label className="small-label">Title</label>
            <input className="full-width-input mb-15" value={cardForm.title} onChange={e => setCardForm({...cardForm, title: e.target.value})} />
            
            <label className="small-label">Description</label>
            <textarea className="full-width-textarea mb-15" value={cardForm.desc} onChange={e => setCardForm({...cardForm, desc: e.target.value})} />
            
            <label className="small-label">External Link URL (Optional)</label>
            <input className="full-width-input mb-15" value={cardForm.link} onChange={e => setCardForm({...cardForm, link: e.target.value})} placeholder="https://..." />

            <label className="small-label">Image {modalMode === 'edit' && "(Leave empty to keep current)"}</label>
            <input type="file" className="mb-15" onChange={e => setCardImage(e.target.files[0])} accept="image/*" />

            <div className="modal-form-actions">
              <button className="btn-secondary" onClick={() => setIsModalOpen(false)}>Cancel</button>
              <button className="btn-success" onClick={handleSaveCard}>
                {modalMode === "create" ? "Create Card" : "Update Card"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}